import React, { useMemo } from "react";
import { AppBar, Avatar, Box, Button, Divider, IconButton, Stack, Toolbar, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../contexts/BrandingContext";
import { useUI } from "../contexts/UIContext";
import { menuItems } from "./Sidebar";

export default function Topbar({ onMenuClick, showMenuButton = true, layoutMode = "SIDEBAR" }) {
	const { user, logout } = useAuth();
	const { branding } = useBranding();
	const { themeMode, toggleThemeMode } = useUI();
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
	const showTopNav = layoutMode === "TOP_NAV";
	const navItems = useMemo(() => {
		if (!user) return [];
		return menuItems.filter((item) => (item.roles || []).includes(user.role));
	}, [user]);

	return (
		<AppBar
			color="transparent"
			elevation={0}
			position="fixed"
			sx={{
				width: isDesktop && showMenuButton ? "calc(100% - 280px)" : "100%",
				ml: isDesktop && showMenuButton ? "280px" : 0,
				backdropFilter: "blur(14px)",
				borderBottom: "1px solid",
				borderColor: "divider",
			}}
		>
			<Toolbar>
				{!isDesktop && showMenuButton && layoutMode === "SIDEBAR" && (
					<IconButton onClick={onMenuClick} color="inherit" sx={{ mr: 1 }}>
						<MenuIcon />
					</IconButton>
				)}
				{!showTopNav ? null : null}
				<Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
					{branding.headerTitle || "Workforce Control Center"}
				</Typography>

				<Tooltip title="Cambia tema">
					<IconButton onClick={toggleThemeMode} color="inherit" sx={{ mr: 1 }}>
						{themeMode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
					</IconButton>
				</Tooltip>

				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mr: 2 }}>
					<Avatar
						sx={{
							bgcolor: branding.colorPrimary || "primary.main",
							backgroundImage: branding.logoUrl ? `url(${branding.logoUrl})` : undefined,
							backgroundSize: "cover",
							backgroundPosition: "center",
							fontFamily: "var(--app-font-family, inherit)",
						}}
					>
						{branding.logoUrl ? "" : (user?.name || "U").charAt(0).toUpperCase()}
					</Avatar>
					<Box sx={{ display: { xs: "none", sm: "block" } }}>
						<Typography variant="body2" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
							{user?.name || "Utente"}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{user?.email || "Nessuna email"}
						</Typography>
					</Box>
				</Box>

				<Button onClick={logout} variant="outlined" color="inherit" startIcon={<LogoutIcon />}>
					Logout
				</Button>
			</Toolbar>
			{showTopNav ? (
				<>
					<Divider sx={{ borderColor: "divider" }} />
					<Box sx={{ px: 2, pb: 1.25, overflowX: "auto" }}>
						<Stack direction="row" spacing={1} sx={{ minWidth: "max-content" }}>
							{navItems.map((item) => (
								<Button
									key={item.path}
									size="small"
									variant={location.pathname === item.path ? "contained" : "text"}
									onClick={() => navigate(item.path)}
									sx={{
										borderRadius: 999,
										px: 1.5,
										fontWeight: 700,
										whiteSpace: "nowrap",
									}}
								>
									{item.label}
								</Button>
							))}
						</Stack>
					</Box>
				</>
			) : null}
		</AppBar>
	);
}
